import type { AutomationDefinitions } from '@tinycld/core/lib/automation/types'
import type { CalendarSchema } from './types'

// calendar_events has no user/owner/author column — the creator is
// `created_by`, which the engine's owner auto-detection does not look for.
// Hence the explicit ownerField.
//
// On its own that scopes personal rules to whoever created the event, which is
// wrong for a shared calendar: a member should have their rules fire for
// events a colleague added. server/automation.go registers an owner resolver
// over calendar_members that supersedes this ownerField; the declaration keeps
// it so the trigger still resolves an owner in a deployment where calendar's
// Go isn't linked (a hosting tenant).
const automation = {
    triggers: [
        {
            id: 'event-added',
            label: 'An event is added',
            collection: 'calendar_events',
            on: 'create',
            ownerField: 'created_by',
            fields: [
                'title',
                'location',
                'start',
                'end',
                { key: 'all_day', label: 'All day' },
                'calendar',
                // Lets a rule tell an event the user authored from one a
                // subscribed feed imported — "notify me about new events, but
                // not the 200 my work calendar just synced".
                { key: 'from_subscription', label: 'From a subscribed feed' },
            ],
        },
        {
            id: 'event-rescheduled',
            label: 'An event is rescheduled',
            collection: 'calendar_events',
            on: 'update',
            ownerField: 'created_by',
            // Only a time change counts. An event whose title or location was
            // edited has not been rescheduled, and firing on those would make
            // "tell me when this moves" useless.
            watch: ['start', 'end'],
            fields: [
                'title',
                'location',
                'start',
                'end',
                { key: 'all_day', label: 'All day' },
                'calendar',
            ],
        },
        {
            id: 'event-removed',
            label: 'An event is removed',
            collection: 'calendar_events',
            on: 'delete',
            ownerField: 'created_by',
            fields: ['title', 'location', 'start', 'end', 'calendar'],
        },
        {
            // Admin hygiene: a subscribed feed that stops syncing goes quiet
            // rather than announcing itself, so the calendar silently drifts
            // out of date. calendar_calendars carries no owner column at all,
            // so this trigger CANNOT auto-detect and has no ownerField to
            // declare — server/automation.go registers a calendar_members
            // resolver for it. A Go filter gates it to a non-empty error, so
            // a successful sync clearing the field doesn't fire it.
            id: 'feed-sync-failed',
            label: 'A calendar feed fails to sync',
            collection: 'calendar_calendars',
            on: 'update',
            watch: ['subscription_error'],
            fields: [
                { key: 'name', label: 'Calendar' },
                { key: 'subscription_error', label: 'Error' },
                { key: 'subscription_url', label: 'Feed URL' },
            ],
        },
    ],
    // Native, not a record-op: v1 params carry no date math, so "three days
    // from now" has to be computed somewhere. The handler takes plain numbers
    // and does the arithmetic.
    actions: [
        {
            id: 'create-event',
            label: 'Create an event',
            kind: 'native',
            params: [
                // NOT a relation param: relationTarget is only resolved for
                // record-op params that name a real column (catalog.go's
                // resolveParam), so a native `type: 'relation'` renders a
                // picker with no target — an empty, unusable menu. Left out
                // entirely instead: the handler files the event on the rule
                // owner's own calendar, which is what the recipes want and
                // needs no input at all.
                { key: 'title', type: 'text', label: 'Title' },
                { key: 'description', type: 'text', label: 'Description' },
                { key: 'starts_in_days', type: 'number', label: 'Starts in (days)' },
                { key: 'duration_minutes', type: 'number', label: 'Duration (minutes)' },
                { key: 'all_day', type: 'boolean', label: 'All day' },
                { key: 'reminder_minutes', type: 'number', label: 'Remind before (minutes)' },
            ],
        },
    ],
} satisfies AutomationDefinitions<CalendarSchema>

export default automation
